/**
 * En qué estado está el anuncio de un producto.
 *
 * «Publicado» no quiere decir «a la venta»: un anuncio termina cuando se vende
 * la última unidad, cuando caduca o cuando lo retiras. Esto traduce lo que
 * responde eBay a algo que se puede pintar, y **deja pasar tal cual lo que no
 * reconoce**: inventar un estado para un valor que no esperábamos sería
 * exactamente el tipo de mentira que este proyecto no cuenta.
 */

// Lo que se considera acabado. El resto de valores conocidos se tratan aparte.
//
// `NOT_FOUND` no lo dice eBay: lo escribe nuestro servidor cuando pregunta por
// la oferta y el destino contesta que ya no existe —porque se borró desde el
// panel de la tienda, por ejemplo—. Antes eso se guardaba como un nulo, igual
// que «todavía no se ha preguntado», y el producto se quedaba en «sin
// comprobar» de por vida.
//
// Esta lista tiene un espejo en `backend/src/routes/analytics.routes.js`.
export const ACABADOS = ['ENDED', 'INACTIVE', 'NOT_FOUND'];

/**
 * ¿Tiene sentido retirar este anuncio de la venta?
 *
 * No es lo mismo que `aLaVenta`, y confundirlos costó un fallo: el botón por
 * producto usaba `aLaVenta` —que es `false` para «publicado, todavía sin
 * comprobar»—, así que no dejaba retirar un anuncio que probablemente estaba
 * vivo. Y el de tanda no comprobaba nada, así que ofrecía retirar un producto
 * que nunca se había publicado.
 *
 * Retirable es: se publicó alguna vez, y no consta que ya haya terminado. Lo
 * que no sabemos no se da por acabado.
 */
export function sePuedeRetirar(item) {
    if (!item?.external_product_id) return false;
    return !ACABADOS.includes(item.listing_status);
}

export function estadoDelAnuncio(item) {
    if (!item?.external_product_id) {
        return { clave: 'sin_publicar', tono: 'badge-warning', borde: '!border-l-amber-500', aLaVenta: false };
    }

    switch (item.listing_status) {
        case 'ACTIVE':
            return { clave: 'activo', tono: 'badge-success', borde: '!border-l-green-500', aLaVenta: true };

        case 'OUT_OF_STOCK':
            return { clave: 'agotado', tono: 'badge-warning', borde: '!border-l-amber-500', aLaVenta: false };

        case 'ENDED':
        case 'INACTIVE':
            return { clave: 'finalizado', tono: 'badge-danger', borde: '!border-l-red-500', aLaVenta: false };

        case 'NOT_FOUND':
            // Se preguntó y ya no está. Es una respuesta, no una duda, y por
            // eso no comparte etiqueta con «sin comprobar».
            return { clave: 'ya_no_esta', tono: 'badge-danger', borde: '!border-l-red-500', aLaVenta: false };

        case null:
        case undefined:
            // Publicado alguna vez y todavía sin preguntar. No se afirma que
            // esté a la venta, pero tampoco lo contrario.
            return { clave: 'sin_comprobar', tono: 'badge-info', borde: '!border-l-slate-400', aLaVenta: false };

        default:
            return {
                clave: 'desconocido',
                crudo: item.listing_status,
                tono: 'badge-info',
                borde: '!border-l-slate-400',
                aLaVenta: false
            };
    }
}

export default estadoDelAnuncio;
